import * as React from 'react';
import { Group, Box, Input, InputProps, Text, createStyles, useMantineTheme } from '@mantine/core';
import { useId } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { NumericFormat, NumberFormatValues } from 'react-number-format';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';
import { InfoTable, IInfoTableRow } from '../../InfoTable';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Types
import { GenericFieldProps } from '../types';
import { SummationLineItem, ROW_TYPES, ICostTableValue } from '../../../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: {
            ...commonFieldStyles.input,
            paddingTop: 0,
            paddingBottom: 0
        },
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        rightGroupPosition: commonFieldStyles.rightGroupPosition,
        leftGroupPosition: commonFieldStyles.leftGroupPosition,
        rightText: {
            // textAlign: 'right'
        },
        leftText: {
            // textAlign: 'right'
        },
        tableHeader: {
            borderBottom: '1px solid #dee2e6'
        },
        rowInput: {
            '&:disabled': {
                border: 'none',
                background: 'transparent'
            }
            // '&:not(:focus)': {
            //     border: 'none',
            //     background: 'transparent'
            // }
        },
        rowInputNoEdit: {
            pointerEvents: 'none'
        },
        fullOpacity: {
            opacity: '1 !important',
            color: '#000 !important'
        },
        tableWrapper: {
            overflow: 'hidden',
            border: '1px solid #cacaca',
            // border: '1px solid #dee2e6',
            borderRadius: '6px'
        },
        noMarginNoPadding: {
            margin: '0',
            padding: '0'
        },
        noBold: {
            fontWeight: 400
        },
        extraBold: {
            fontWeight: 700
        },
        noEdit: {
            color: '#000 !important',
            opacity: '1 !important',
            fontWeight: 700,
            cursor: 'default !important'
            // pointerEvents: 'auto !important'
        },
        bold: {
            fontWeight: 500
        },
        fullWidth: {
            width: '100%'
        },
        customInputSpacing: {
            marginTop: 'calc(0.625rem / 2)',
            marginBottom: 'calc(0.625rem / 2)',
            paddingBottom: '0'
            // paddingBottom: '0.5rem'
        },
        chevronIcon: {
            transition: 'all 0.2s ease'
        },
        iconRotated: {
            transform: 'rotate(90deg)'
        },
        totalInputPrefix: {
            borderRadius: '0.25rem',
            border: '0.0625rem solid #ced4da',
            paddingLeft: '10px',
            paddingRight: '10px',
            fontSize: '0.85rem',
            color: theme.colors.gray[6]
        },
        totalInputWrapper: {
            flexGrow: 1
        },
        totalInput: {
            borderLeft: 'none',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0
        },
        inputGroup: {
            gap: 0,
            width: '100%',
            paddingBottom: '0.5rem'
        },
        errorText: {
            color: '#fa5252'
        }
    };
});

export interface InputValueTableProps extends InputProps, GenericFieldProps {
    key?: string;
    tableKey?: string;
    label?: string;
    readOnly?: boolean;
    validateOnChange?: boolean;
    totalHighlight?: boolean;
    noHeader?: boolean;
    blueHeader?: boolean;
    placeholder?: string;
    background?: string;
    onChange?: (value: ICostTableValue) => void;
    value?: ICostTableValue;
    data?: SummationLineItem[];
    updateValue?: ({ newValue, key }: { newValue?: number; key: string }) => void;
    errors?: Record<string, string | null> | null;
    validator?: () => Record<string, string | null> | string | null;
    rightAlignSecondCol?: boolean;
}

export const InputValueTable: React.FC<InputValueTableProps> = ({
    readOnly,
    labelKey,
    label,
    required,
    tableKey,
    descriptionKey,
    tooltipKey,
    onChange,
    validateOnChange,
    totalHighlight,
    value = {
        total: 0,
        items: []
    },
    data,
    placeholder,
    parentform,
    targetperiod,
    updateValue,
    errors,
    validator,
    noHeader,
    rightAlignSecondCol,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const rows = React.useMemo(() => {
        return [
            ...(data ?? []).map(({ label, value, key, id, type, hidden, displayAsNegative }, index) => ({
                key,
                type,
                hidden,
                displayAsNegative,
                label: (
                    <Text
                        className={cx('', {
                            [classes.extraBold]: [ROW_TYPES.TOTAL, ROW_TYPES.SUM_OF].includes(type),
                            [classes.errorText]: errors?.[key]
                        })}
                    >
                        {label}
                    </Text>
                ),
                value: (
                    <Input.Wrapper
                        classNames={{
                            root: classes.root
                        }}
                    >
                        <Input<any>
                            component={NumericFormat}
                            disabled={type !== ROW_TYPES.AMOUNT}
                            classNames={{
                                input: cx(classes.input, {
                                    [classes.rowInput]: true,
                                    [classes.noBold]: true,
                                    [classes.noEdit]: [ROW_TYPES.TOTAL, ROW_TYPES.SUM_OF].includes(type),
                                    [classes.fullOpacity]: type === ROW_TYPES.READ_ONLY
                                }),
                                icon: classes.icon,
                                wrapper: cx(classes.wrapper, {
                                    [classes.noMarginNoPadding]: true
                                })
                            }}
                            id={id}
                            decimalScale={2}
                            fixedDecimalScale
                            prefix={`${displayAsNegative ? '-' : ''}$ `}
                            thousandSeparator={','}
                            placeholder={
                                type === ROW_TYPES.READ_ONLY
                                    ? '--'
                                    : t('inputValueTable.placeholders.amount', { ns: 'components' }) ?? ''
                            }
                            onValueChange={(values: NumberFormatValues) => {
                                updateValue?.({
                                    newValue: values.floatValue ?? undefined,
                                    key
                                });
                                // if (validateOnChange) {
                                //     validator?.();
                                // }
                            }}
                            value={type === ROW_TYPES.READ_ONLY && typeof value === 'undefined' ? '--' : value}
                            error={errors?.[key]}
                        />
                    </Input.Wrapper>
                )
            }))
        ].filter(Boolean) as IInfoTableRow[];
    }, [value]);

    return (
        <Box
            className={cx(classes.tableWrapper, {
                [classes.customInputSpacing]: true
            })}
        >
            <InfoTable
                blueHeader={props?.blueHeader}
                background={props?.background}
                rows={rows}
                headerCells={noHeader ? [] : ['Label', 'Amount']}
                rowActions={false}
                totalHighlight={totalHighlight}
                rightAlignSecondCol={rightAlignSecondCol}
            />
        </Box>
    );
};
